/* One frame per subject, from the subject layer plus the authored geometry.
 * The padding is asymmetric on purpose: the grid leaves 32px between rows, and
 * the chip needs a line of it. */
import { EXTRA_EDGES, NODES } from '../data/nodes';
import { TRACKS } from '../data/routes';
import type { NodeDef } from '../types';

export interface Cluster {
  id: string;
  labelKey: string;
  x: number;
  y: number;
  w: number;
  h: number;
  nodes: string[];
  chapters: string[];
}

const PAD = 12;
const TOP = 26;
const BOTTOM = 0;

/* The caption is one 11px line: 13px tall, and it needs that much room inside the
 * band above a card. The band is TOP world px, so below minZoom it cannot fit —
 * the caption is hidden there and the rail carries the grouping instead. */
export const CAPTION = { height: 13, minZoom: 0.55 };

export function clusters(): Cluster[] {
  const byId = new Map(NODES.map((n) => [n.id, n]));
  return TRACKS.map((track) => {
    const nodes = [
      ...track.chapters,
      ...EXTRA_EDGES.filter((e) => track.chapters.includes(e[0])).map((e) => e[1]),
    ];
    const boxes = nodes.map((id) => byId.get(id)).filter((n): n is NodeDef => !!n);
    const x = Math.min(...boxes.map((n) => n.x)) - PAD;
    const y = Math.min(...boxes.map((n) => n.y)) - TOP;
    const x2 = Math.max(...boxes.map((n) => n.x + n.w)) + PAD;
    const y2 = Math.max(...boxes.map((n) => n.y + n.h)) + BOTTOM;
    return {
      id: track.id,
      labelKey: track.labelKey,
      x,
      y,
      w: x2 - x,
      h: y2 - y,
      nodes,
      chapters: track.chapters,
    };
  }).sort((a, b) => a.y - b.y || a.x - b.x);
}
